import { isObject, find, findBetween, getTimestamp } from './customMethods'

// General
const isPrivateMsg = (msg) => msg?.private ?? false
const isClientMsg = (msg) => msg?.by === 'consumer' ?? false
const isAgentMsg = (msg) => msg?.by === 'agent' ?? false
const isManagerMsg = (msg) => msg?.by === 'manager' ?? false
const isEmployeeMsg = (msg) => isAgentMsg(msg) || isManagerMsg(msg)
const isAllowedUserMsg = (msg) => isAgentMsg(msg) || isClientMsg(msg)
const isSameSender = (msg, sender) => msg?.by === sender ?? false

// SLA status
const isInactive = (e) => e?.status === 'inactive' && true
const isBursting = (e) => e?.status === 'bursting' && true
const isOverdue = (e) => e?.status === 'overdue' && true
const isNormal = (e) => e?.status === 'normal' && true
const isClosed = (e) => e?.status === 'closed' || e?.status === 'pos-chat'

// get the message that will control user idle events
const findIdleMsg = (chat, sender) => {
  if (
    isObject(chat) &&
    chat.status !== 'closed' &&
    chat.status !== 'pos-chat'
  ) {
    const msgs = [...chat?.message]

    // Set default idle message in case of payload comes without a valid message (client or agent)
    const defaultMsg = {
      text: 'start',
      by: 'consumer',
      sentTime: chat?.assignedTime,
    }

    sender = sender ?? find(msgs, isAllowedUserMsg, true)?.by ?? defaultMsg.by

    if (sender) {
      // Handle message interval between the idle message and current candidate
      const hasOtherUserMsg = (srcArray, curIdleMsg, msg) => {
        const start = srcArray.indexOf(curIdleMsg) + 1
        const end = srcArray.indexOf(msg)
        const func = (m) => isAllowedUserMsg(m) && !isSameSender(m, sender)
        return findBetween(srcArray, start, end, func)
      }

      const reduceMethod = (curIdleMsg, msg, index, srcArray) => {
        if (isSameSender(msg, sender)) {
          if (
            !isSameSender(curIdleMsg, sender) ||
            hasOtherUserMsg(srcArray, curIdleMsg, msg)
          ) {
            curIdleMsg = msg
          }
        }
        return curIdleMsg
      }

      const firstMsg = isSameSender(msgs[0], sender) ? msgs[0] : defaultMsg
      const idleMsg = [...msgs]?.reduce(reduceMethod, firstMsg)

      if (getTimestamp(idleMsg?.sentTime) <= getTimestamp(chat?.assignedTime))
        idleMsg.sentTime = chat.assignedTime

      return idleMsg?.by === sender ? idleMsg : {}
    }
  }
  return {}
}

const shouldUpdateIdleMsg = (chat, msg) => {
  const sender = msg?.by
  return (
    (sender &&
      isObject(chat) &&
      isAllowedUserMsg(msg) &&
      !isSameSender(chat?.idleMessage, sender)) ??
    false
  )
}

const getTriggers = (chat) => {
  if (isObject(chat)) {
    if (
      chat?.slaLate &&
      chat?.inactivityTime &&
      chat?.idleMessage?.sentTime &&
      isAgentMsg(chat?.lastMessage)
    ) {
      const msgTime = new Date(chat?.lastMessage?.sentTime).getTime()
      const offsetBursting = chat?.slaLate / 2;
      let overdue = null
      let bursting = null
      let inactive = null

      if (isAgentMsg(chat?.lastMessage)) {
        overdue = msgTime + parseInt(chat.slaLate) * 1000
        bursting = overdue - offsetBursting * 1000
      }

      return {
        sla: chat?.slaLate,
        overdue: overdue,
        bursting: bursting,
        inactive: inactive,
      }
    }
  }
  return null
}

const handleSLA = (triggers) => {
  if (isObject(triggers)) {
    const now = Date.now()
    const { overdue, bursting, inactive } = triggers
    return overdue && now > overdue
      ? 'overdue'
      : overdue && bursting && now > bursting && now <= overdue
      ? 'bursting'
      : (bursting && now <= bursting) || (inactive && now <= inactive)
      ? 'normal'
      : 'inactive'
  }
  return 'closed'
}

const sortConversation = (a, b, automatic) => {
  const assignedA = a?.assignedTime && getTimestamp(a.assignedTime)
  const assignedB = b?.assignedTime && getTimestamp(b.assignedTime)
  const endA = a?.end && getTimestamp(a.end)
  const endB = b?.end && getTimestamp(b.end)

  const triggerOverdueA = a?.statusTriggers?.overdue
  const triggerOverdueB = b?.statusTriggers?.overdue
  const triggerInactiveA = a?.statusTriggers?.inactive
  const triggerInactiveB = b?.statusTriggers?.inactive

  const inactiveA = isInactive(a)
  const inactiveB = isInactive(b)
  const closedA = isClosed(a)
  const closedB = isClosed(b)

  if (closedA || closedB) {
    if (closedA && !closedB) return 1
    else if (!closedA && closedB) return -1
    else return endA > endB ? -1 : endA === endB ? 0 : 1
  } else if (inactiveA || inactiveB) {
    if (inactiveA && !inactiveB) return 1
    else if (!inactiveA && inactiveB) return -1
    else
      return triggerInactiveA < triggerInactiveB
        ? 1
        : triggerInactiveA > triggerInactiveB
        ? -1
        : 0
  } else {
    if (automatic) {
      if (triggerOverdueA > triggerOverdueB) return 1
      else if (triggerOverdueA < triggerOverdueB) return -1
      else return 0
    } else {
      return assignedA > assignedB ? -1 : assignedA < assignedB ? 1 : 0
    }
  }
}

const getSender = (msg, userList) => {
  return userList?.find((user) => msg?.userId === user?.user_id)?.user_name
}

// Exports
export {
  isPrivateMsg,
  isClientMsg,
  isAgentMsg,
  isManagerMsg,
  isEmployeeMsg,
  isAllowedUserMsg,
  isSameSender,
  findIdleMsg,
  shouldUpdateIdleMsg,
  getTriggers,
  handleSLA,
  sortConversation,
  getSender,
}
